import { goto } from "$app/navigation"
import { RestError } from "$lib"
import { isProduction } from "$lib/environment"
import { getIsWebView } from "$lib/util"
import * as Sentry from "@sentry/sveltekit"
import { InvalidSessionError } from "@tastyworks/tastyworks-api"
import { get, writable } from "svelte/store"

// TODO: IS-915 - remove once native mobile app releases full cobrowse support
function maybeInitCobrowse() {
  if (!get(getIsWebView())) {
    import("@tastyworks/shared-cobrowse/cobrowse").then(({ initCobrowse }) =>
      initCobrowse({ license: import.meta.env.VITE_COBROWSE_LICENSE_KEY })
    )
  }
}

maybeInitCobrowse()

if (isProduction()) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    tracesSampleRate: 0.1,
    release: __APP_VERSION__,
    ignoreErrors: [
      // this is a rare race condition that may occur during polling on the review page. It is not impactful to user experience
      "Failed to fetch TypeError ../../../../../../../../packages/api/src/tastyworks/http.ts this.fetchWithErrorHandler this.fetchWithErrorHandler(packages/api/src/tastyworks/http)",
      // this comes from GTM and is not impactful to user experience
      "Failed to fetch TypeError /gtag/js Mc Mc(gtag/js)",
      // This is an expected error from our app and should be ignored.
      "Invalid Session",
      // This is an expected error according to sentry source code (quoted below)
      // NOTE: If you are a Sentry user, and you are seeing this stack frame, it
      // means the sentry.javascript SDK caught an error invoking your application code. This
      // is expected behavior and NOT indicative of a bug with sentry.javascript.
      "Cookies is not defined",
      "Can't find variable: Cookies",
    ],
    integrations: [
      Sentry.extraErrorDataIntegration(),
      Sentry.captureConsoleIntegration({
        levels: ["assert"],
      }),
    ],
    beforeSend(event, hint) {
      const { originalException } = hint

      if (originalException instanceof RestError) {
        event.message = getRestErrorMessage(originalException)
      }

      return event
    },
  })
}

function getRestErrorMessage(originalException: unknown) {
  let reportableErrorMessage = ""

  if (originalException instanceof RestError) {
    if (
      originalException.errorContainer &&
      !originalException.errorContainer.isEmpty()
    ) {
      reportableErrorMessage = originalException.errorContainer.toString()
    } else {
      reportableErrorMessage = originalException.message
    }
  }

  return reportableErrorMessage
}

function redirectToSignup() {
  sessionStorage.clear()
  let target = "/signup"
  if (window && window.location.search) {
    target += window.location.search
  }
  return goto(target)
}

async function _handleError({ error }: any): Promise<App.Error | undefined> {
  console.error(error)
  if (error instanceof InvalidSessionError) {
    await redirectToSignup()
    return
  } else if (error instanceof RestError) {
    return {
      message: error.errorContainer?.toString() ?? error.message,
    }
  }

  // What handleError returns becomes exposed in $page.error.
  return {
    message: "Internal Server",
  }
}

export const handleError = isProduction()
  ? Sentry.handleErrorWithSentry(_handleError)
  : _handleError

if (import.meta.vitest) {
  const { vi, describe, it, expect, beforeEach } = import.meta.vitest

  vi.mock("$app/navigation", () => ({
    goto: vi.fn(),
  }))

  vi.mock("@tastyworks/shared-cobrowse/cobrowse", () => ({
    initCobrowse: vi.fn(),
  }))

  vi.mock("$lib/util", () => ({
    getIsWebView: vi.fn(),
  }))

  describe("redirectToSignup", () => {
    beforeEach(() => {
      vi.clearAllMocks()
      Object.defineProperty(window, "location", {
        value: { search: "" },
        writable: true,
      })
      Object.defineProperty(window, "sessionStorage", {
        value: {
          clear: vi.fn(),
        },
        writable: true,
      })
    })

    it("should clear sessionStorage and redirect to /signup", async () => {
      await redirectToSignup()

      expect(sessionStorage.clear).toHaveBeenCalled()
      expect(vi.mocked(goto)).toHaveBeenCalledWith("/signup")
    })

    it("should preserve query parameters in redirect", async () => {
      Object.defineProperty(window, "location", {
        value: { search: "?utm_source=email&campaign=test" },
        writable: true,
      })

      await redirectToSignup()

      expect(sessionStorage.clear).toHaveBeenCalled()
      expect(vi.mocked(goto)).toHaveBeenCalledWith(
        "/signup?utm_source=email&campaign=test"
      )
    })

    it("should handle empty search params", async () => {
      Object.defineProperty(window, "location", {
        value: { search: "" },
        writable: true,
      })

      await redirectToSignup()

      expect(sessionStorage.clear).toHaveBeenCalled()
      expect(vi.mocked(goto)).toHaveBeenCalledWith("/signup")
    })
  })

  describe("maybeInitCobrowse", () => {
    let initCobrowseMock: ReturnType<typeof vi.fn>

    beforeEach(async () => {
      vi.clearAllMocks()
      const cobrowseModule = await import(
        "@tastyworks/shared-cobrowse/cobrowse"
      )
      initCobrowseMock = vi.mocked(cobrowseModule.initCobrowse)
    })

    it.each([
      [true, false],
      [false, true],
    ])(
      "isWebView=%s → initCobrowse called: %s",
      async (isWebView, shouldInit) => {
        const { getIsWebView } = await import("$lib/util")
        vi.mocked(getIsWebView).mockReturnValue(writable(isWebView))

        maybeInitCobrowse()
        await vi.waitFor(() => {
          if (shouldInit) {
            expect(initCobrowseMock).toHaveBeenCalledOnce()
          } else {
            expect(initCobrowseMock).not.toHaveBeenCalled()
          }
        })
      }
    )
  })
}
